import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Home,
  LogOut,
  Menu,
  X,
  FileText,
  Settings,
  ArrowLeft,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  FileWarning,
  DollarSign,
  Paperclip,
  Shield,
  ChevronDown,
  ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const claimStatusConfig = {
  draft: { label: "Brouillon", color: "bg-gray-100 text-gray-700", icon: FileText },
  open: { label: "En cours de traitement", color: "bg-blue-100 text-blue-700", icon: Clock },
  refund: { label: "Remboursé", color: "bg-green-100 text-green-700", icon: DollarSign },
  refund_repeal: { label: "Rétractation", color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  uncomplete: { label: "Incomplet - documents manquants", color: "bg-orange-100 text-orange-700", icon: FileWarning },
  refused: { label: "Refusé", color: "bg-red-100 text-red-700", icon: XCircle },
  closed: { label: "Clôturé", color: "bg-slate-100 text-slate-600", icon: CheckCircle } };

const ClaimsPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [claims, setClaims] = useState([]);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [declaring, setDeclaring] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [expandedClaim, setExpandedClaim] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [propsResp, claimsResp] = await Promise.all([
        axios.get(`${API_URL}/api/properties/`, { withCredentials: true }),
        axios.get(`${API_URL}/api/claims/property/${propertyId}`, { withCredentials: true }),
      ]);
      const prop = propsResp.data.find((p) => p.id === propertyId);
      setProperty(prop || null);
      setClaims(claimsResp.data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404) {
        toast.error("Logement non trouvé");
        navigate("/logements");
      }
    } finally {
      setLoading(false);
    }
  }, [propertyId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeclare = async () => {
    setDeclaring(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/claims/property/${propertyId}/declare`,
        {},
        { withCredentials: true }
      );
      if (data.claim_form_url) {
        window.open(data.claim_form_url, "_blank", "noopener,noreferrer");
        toast.success("Le formulaire de déclaration s'ouvre dans un nouvel onglet");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de récupérer le lien de déclaration");
    } finally {
      setDeclaring(false);
    }
  };

  const handleSync = async () => {
    if (!property?.contract?.id) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/claims/sync/${property.contract.id}`,
        {},
        { withCredentials: true }
      );
      toast.success(data.message);
      await fetchData();
    } catch (err) {
      toast.error("Erreur de synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric" });
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "-";
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);
  };

  const riskAddress = property?.risk_address || {};
  const addressStr = `${riskAddress.street || ""}, ${riskAddress.postal_code || ""} ${riskAddress.city || ""}`;
  const isActive = property?.status === "active" && property?.contract?.status === "active";

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans']">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-600">
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-2">
            {user?.role === "admin" && (
              <Link to="/admin" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
                <Shield className="w-5 h-5" />
                Administration
              </Link>
            )}
            <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
              <LayoutDashboard className="w-5 h-5" />
              Tableau de bord
            </Link>
            <Link to="/logements" className="flex items-center gap-3 px-4 py-3 text-[#19294C] bg-slate-100 rounded-lg font-medium">
              <Home className="w-5 h-5" />
              Mes logements
            </Link>
            <button className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg w-full text-left">
              <FileText className="w-5 h-5" />
              Mes commandes
            </button>
            <button className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg w-full text-left">
              <Settings className="w-5 h-5" />
              Paramètres
            </button>
          </nav>
          <div className="p-4 border-t border-slate-100">
            <div className="px-4 py-3 mb-2">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg w-full text-left">
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600">
                <Menu className="w-6 h-6" />
              </button>
              <button onClick={() => navigate("/logements")} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm">
                <ArrowLeft className="w-4 h-4" />
                Mes logements
              </button>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8" data-testid="claims-page-content">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00B8E4]" />
            </div>
          ) : !property ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Logement non trouvé</p>
            </div>
          ) : (
            <>
              {/* Property Header */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']" data-testid="claims-property-name">
                      Sinistres - {property.name}
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">{addressStr}</p>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={handleSync}
                      disabled={syncing || !property.contract?.meetch_reference}
                      data-testid="claims-sync-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                      Synchroniser
                    </Button>
                    {isActive && (
                      <Button
                        className="bg-[#00B8E4] hover:bg-[#15803d] text-white"
                        onClick={handleDeclare}
                        disabled={declaring}
                        data-testid="claims-declare-btn"
                      >
                        {declaring ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-2" />
                        )}
                        Déclarer un sinistre
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Claims List */}
              {claims.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">Aucun sinistre déclaré</h3>
                  <p className="text-slate-500 text-sm max-w-md mx-auto">
                    {isActive
                      ? "Pour déclarer un sinistre, cliquez sur le bouton ci-dessus. Vous serez redirigé vers le formulaire de déclaration de votre assureur."
                      : "Les sinistres ne peuvent être déclarés que pour les logements avec une protection active."}
                  </p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="claims-list">
                  {claims.map((claim) => {
                    const cfg = claimStatusConfig[claim.meetch_status] || claimStatusConfig.draft;
                    const StatusIcon = cfg.icon;
                    const isExpanded = expandedClaim === claim.id;

                    return (
                      <div key={claim.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`claim-card-${claim.id}`}>
                        {/* Claim Header */}
                        <button
                          className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                          onClick={() => setExpandedClaim(isExpanded ? null : claim.id)}
                          data-testid={`claim-toggle-${claim.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cfg.color.split(" ")[0]}`}>
                              <StatusIcon className={`w-5 h-5 ${cfg.color.split(" ")[1]}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-3">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                                  {claim.status_label || cfg.label}
                                </span>
                                <span className="text-xs text-slate-400 font-mono">{claim.meetch_claim_id}</span>
                              </div>
                              <p className="text-sm text-slate-500 mt-1">
                                Déclaré le {formatDate(claim.created_at)}
                                {claim.issue_date && ` | Incident du ${formatDate(claim.issue_date)}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {claim.refund_amount > 0 && (
                              <span className="text-sm font-medium text-green-600">{formatAmount(claim.refund_amount)}</span>
                            )}
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                          </div>
                        </button>

                        {/* Expanded Detail */}
                        {isExpanded && (
                          <div className="px-6 pb-6 border-t border-slate-100 pt-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                              {claim.comment && (
                                <div className="col-span-full">
                                  <p className="text-xs text-slate-500 mb-1">Commentaire</p>
                                  <p className="text-sm text-slate-800 bg-slate-50 rounded-lg p-3">{claim.comment}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-xs text-slate-500">Date de l'incident</p>
                                <p className="text-sm font-medium">{formatDate(claim.issue_date)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Date de traitement</p>
                                <p className="text-sm font-medium">{formatDate(claim.processed_at)}</p>
                              </div>
                              {claim.requested_amount !== null && claim.requested_amount !== undefined && (
                                <div>
                                  <p className="text-xs text-slate-500">Montant demandé</p>
                                  <p className="text-sm font-medium">{formatAmount(claim.requested_amount)}</p>
                                </div>
                              )}
                              {claim.refund_amount !== null && claim.refund_amount !== undefined && claim.refund_amount > 0 && (
                                <div>
                                  <p className="text-xs text-slate-500">Montant remboursé</p>
                                  <p className="text-sm font-medium text-green-600">{formatAmount(claim.refund_amount)}</p>
                                </div>
                              )}

                              {/* Refused message */}
                              {claim.meetch_status === "refused" && (
                                <div className="col-span-full bg-amber-50 border border-amber-200 rounded-lg p-4">
                                  <p className="text-sm text-amber-800">
                                    Votre déclaration n'a pas pu être acceptée à ce stade.
                                    {claim.refusal_reason && <span className="block mt-1 italic">{claim.refusal_reason}</span>}
                                  </p>
                                  <p className="text-sm text-amber-700 mt-2">
                                    Pour toute question : <strong>support@hostprotect.io</strong>
                                  </p>
                                </div>
                              )}

                              {/* Uncomplete message */}
                              {claim.meetch_status === "uncomplete" && (
                                <div className="col-span-full bg-orange-50 border border-orange-200 rounded-lg p-4">
                                  <p className="text-sm text-orange-800">
                                    Des documents complémentaires sont nécessaires. Veuillez compléter votre dossier via le formulaire de l'assureur.
                                  </p>
                                  {isActive && (
                                    <Button
                                      size="sm"
                                      className="mt-3 bg-orange-500 hover:bg-orange-600 text-white"
                                      onClick={handleDeclare}
                                      disabled={declaring}
                                    >
                                      <ExternalLink className="w-3.5 h-3.5 mr-2" />
                                      Accéder au formulaire
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Attachments */}
                            {claim.attachments && claim.attachments.length > 0 && (
                              <div>
                                <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                                  <Paperclip className="w-3.5 h-3.5" />
                                  Pièces jointes ({claim.attachments.length})
                                </p>
                                <div className="space-y-2">
                                  {claim.attachments.map((att, i) => (
                                    <a
                                      key={att.id || i}
                                      href={att.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                                    >
                                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-slate-700 truncate">{att.filename || "Document"}</p>
                                        <p className="text-xs text-slate-400">{att.file_type} {att.proof_type ? `| ${att.proof_type}` : ""}</p>
                                      </div>
                                      <ExternalLink className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default ClaimsPage;
